import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSSE, type SSEEvent } from './useSSE'
import { queryKeys } from '@/lib/queryKeys'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useSseStatusStore } from '@/stores/useSseStatusStore'
import type { NotificationType } from '@/types/notification'

const STREAM_ID = 'notifications'

const NOTIFICATION_TYPES = new Set<string>([
  'PLAN_SHARED',
  'OWNERSHIP_TRANSFERRED',
  'APPROVAL_REQUIRED',
  'APPROVAL_DECISION',
  'SOURCING_REQUEST_ASSIGNED',
  'SOURCING_OPTION_ADDED',
  'MILESTONE_APPROACHING',
  'MILESTONE_OVERDUE',
  'COMMENT_ADDED',
  'COMMENT_MENTIONED',
  'PLAN_UNSUBMITTED',
  'PLAN_UNAPPROVED',
  'HIERARCHY_SHARED',
])

const ERROR_THROTTLE_MS = 300_000

/**
 * Invalidate relevant query caches based on the notification type so that
 * pages the user is currently viewing reflect the change in real time.
 *
 * React Query only refetches invalidated queries that have active observers,
 * so broad invalidation here is safe — no unnecessary network requests.
 */
function invalidateForNotificationType(
  queryClient: ReturnType<typeof useQueryClient>,
  type: NotificationType,
  data: Record<string, unknown> | null | undefined
) {
  const planId =
    (data?.plan_id as string | undefined) ??
    (data?.entity_type === 'plan'
      ? (data?.entity_id as string | undefined)
      : undefined)
  const versionId = data?.version_id as string | undefined

  switch (type) {
    case 'COMMENT_ADDED':
    case 'COMMENT_MENTIONED': {
      const entityType = data?.entity_type as string | undefined
      const entityId = data?.entity_id as string | undefined
      const nestedData = data?.data as Record<string, unknown> | undefined
      const lineRefId =
        (data?.line_ref_id as string | undefined) ??
        (nestedData?.line_ref_id as string | undefined) ??
        (entityType === 'line_item' ? entityId : undefined)

      if (entityType === 'line_item' && lineRefId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.lineItemComments.byRefId(lineRefId),
        })
        if (planId) {
          queryClient.invalidateQueries({
            queryKey: ['dag-table', 'rows', planId],
          })
        }
      } else if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.comments.byPlanId(planId),
        })
      }
      break
    }

    case 'APPROVAL_DECISION':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.submissions.byPlanId(planId),
        })
      }
      break

    case 'OWNERSHIP_TRANSFERRED':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      break

    case 'APPROVAL_REQUIRED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.manager.all,
      })
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
      }
      break

    case 'SOURCING_OPTION_ADDED':
      if (planId && versionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
        })
      }
      break

    case 'PLAN_UNSUBMITTED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.manager.all,
      })
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.submissions.byPlanId(planId),
        })
      }
      break

    case 'PLAN_UNAPPROVED':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.submissions.byPlanId(planId),
        })
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      break

    case 'PLAN_SHARED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      if (planId) {
        // The share may carry a freeze grant; refresh can_freeze if open.
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
      }
      break

    case 'HIERARCHY_SHARED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.hierarchyShares.all,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.browse.all,
      })
      break

    case 'MILESTONE_APPROACHING':
    case 'MILESTONE_OVERDUE':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: ['plan-detail', 'calendar', planId],
        })
      }
      break

    // SOURCING_REQUEST_ASSIGNED: no additional invalidation needed
  }
}

export function useNotificationSSE() {
  const queryClient = useQueryClient()
  const incrementUnreadCount = useNotificationStore(s => s.incrementUnreadCount)
  const lastErrorInvalidationRef = useRef(0)

  const handleMessage = useCallback(
    (event: SSEEvent) => {
      const payload = event.data as Record<string, unknown> | undefined
      const notificationType = payload?.type as string | undefined

      // Only process actual notification events, skip keepalives/pings
      if (!notificationType || !NOTIFICATION_TYPES.has(notificationType)) {
        return
      }

      incrementUnreadCount()
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      })

      invalidateForNotificationType(
        queryClient,
        notificationType as NotificationType,
        payload
      )
    },
    [queryClient, incrementUnreadCount]
  )

  const handleReconnect = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.notifications.all,
    })
  }, [queryClient])

  const handleError = useCallback(() => {
    const now = Date.now()
    if (now - lastErrorInvalidationRef.current >= ERROR_THROTTLE_MS) {
      lastErrorInvalidationRef.current = now
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      })
    }
  }, [queryClient])

  const handleHealthChange = useCallback((healthy: boolean) => {
    useSseStatusStore.getState().setHealthy(STREAM_ID, healthy)
  }, [])

  useEffect(() => {
    const unregister = useSseStatusStore.getState().unregister
    return () => {
      unregister(STREAM_ID)
    }
  }, [])

  useSSE({
    url: '/notifications/events',
    streamId: STREAM_ID,
    onMessage: handleMessage,
    onError: handleError,
    onReconnect: handleReconnect,
    onHealthChange: handleHealthChange,
    retry: true,
    retryInterval: 3000,
    maxRetryInterval: 300_000,
    autoDisconnectOnTerminal: false,
    openWhenHidden: false,
  })
}
